import create from 'zustand';
import { mountStoreDevtool } from 'simple-zustand-devtools';
import dayjs from 'dayjs';
import {
  fullListCols,
  chairListCols,
  picsListCols,
  simpleListCols,
} from '../helpers/columns';
import CustomParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(CustomParseFormat);

const useStore = create((set, get) => ({
  // STATE VALUES
  // Define state values and defaults
  view: 'list',
  loading: true,
  gridOptions: { listType: 'fulllist', rowHeight: 50 },
  filters: { search: '', date: '', country: '', type: '' },
  statements: [{}],
  slots: [],
  countries: [],
  order: [{}],
  loaded_slots: [{}],
  settings: {
    ajaxurl: 'https://plenipotprod.local.dev/en/wp-json/itu-wp-api/v1/',
    is_multisite: '',
    current_blog: 1,
    localize_index: 0,
    params: {
      listtype: 'fulllist',
      displayviewswitch: true,
      viewtype: 'card',
    },
  },
  MUIdata: { rows: [], columns: [] },

  // METHODS
  // State methods
  // Toggle loading state
  toggleLoading: () => {
    set((state) => ({ ...state, loading: !state.loading }));
  },

  // Set a filter value
  setFilter: (key, value) => {
    set((state) => ({ ...state, filters: { ...state.filters, [key]: value } }));
    get().buildMUIData();
  },

  // Set the view value
  changeView: (viewname) => {
    set((state) => ({ ...state, view: viewname }));
  },

  // Set the list type value
  changeListType: (type) => {
    const rowHeight = type === 'piclist' ? 100 : 50;
    set((state) => ({
      ...state,
      gridOptions: { ...state.gridOptions, ...{ listType: type, rowHeight } },
    }));

    // Rebuild the MUI datatable data
    get().buildMUIData();
  },

  // Data manipulation methods
  // Update the settings if in a wp-environment from the localize script data
  updateSettings: () => {
    const wpSettings = window.itu_policy_statements;
    if (wpSettings) {
      set((state) => ({ ...state, settings: wpSettings }));
      get().changeListType(wpSettings.params.listtype);
      get().changeView(wpSettings.params.viewtype);
    } else {
      console.log('No wp settings found');
    }
  },

  // Get the statements data from the REST API
  updateData: async () => {
    // Set REST API endpoints
    const statementsEndpoint = get().settings.ajaxurl + 'policy_statements';
    const statementsOrderEndpoint =
      get().settings.ajaxurl + 'policy_statements_order';
    const slotsEndpoint = get().settings.ajaxurl + 'policy_slots';

    // Set statements and statement related store parts
    await fetch(statementsEndpoint)
      .then((res) => res.json())
      .then((data) => {
        let countries = [];
        data.forEach((statement) => {
          if (
            !countries.includes(statement.fields.country) &&
            statement.fields.country
          ) {
            countries.push(statement.fields.country);
          }
        });
        countries.sort();
        set((state) => ({ ...state, countries }));
        set((state) => ({ ...state, statements: data }));
      });

    // Set statement order store part
    await fetch(statementsOrderEndpoint)
      .then((res) => res.json())
      .then((data) => set({ order: data }));

    // Set slots store part
    await fetch(slotsEndpoint)
      .then((res) => res.json())
      .then((data) => {
        let slotsforDropDown = [];
        data.forEach((slot) => {
          slotsforDropDown.push(
            `${dayjs(slot.timestamp).format('DD/MM/YYYY')} ${slot.halfday}`
          );
        });
        set((state) => ({ ...state, slots: slotsforDropDown }));
        set((state) => ({ ...state, loaded_slots: data }));
      });

    // Rebuild the MUI datatable data
    get().buildMUIData();
    // Toggle loading state
    set((state) => ({ ...state, loading: false }));
  },

  // Build the MUI datatable data
  buildMUIData: async () => {
    // Set loading to true
    get().toggleLoading();

    // Define empty data grid
    let dataGrid = { rows: [], columns: [] };

    // Function to return formatted assigned slot using dayjs
    const getAssignedSlot = (columnId) => {
      const slot = get().loaded_slots.find(
        (slot) => slot.columnName === columnId
      );
      return dayjs(slot.timestamp).format('DD/MM/YYYY') + ` ${slot.halfday}`;
    };

    // Build datagrid columns depending on the list type. List types are defined in /helpers/columns.js
    switch (get().gridOptions.listType) {
      case 'chairlist':
      case 'card':
        dataGrid.columns = chairListCols;
        break;
      case 'piclist':
        dataGrid.columns = picsListCols;
        break;
      case 'simplelist':
        dataGrid.columns = simpleListCols;
        break;
      default:
        dataGrid.columns = fullListCols;
        break;
    }

    // Loop through the slots order
    const order = get().order;
    order.length > 0 &&
      order.forEach((slot) => {
        // Omit the unassigned statements
        if (slot.columnId !== 'unassigned') {
          // Loop through the slot statements
          slot.statements &&
            slot.statements.forEach((statement, index) => {
              // Get the current statement from the statements store
              const currentStatement = get().statements.find(
                (s) => s.ID === statement
              );
              // Define a new row with columns common to all list types
              let newRow = {
                index: index + 1,
                id: currentStatement.ID,
                country: `${currentStatement.fields.country}`,
                speaker: `${currentStatement.fields.prefix} ${
                  currentStatement.fields.first_name
                } ${currentStatement.fields.last_name.toUpperCase()}`,
                speaker_title: currentStatement.fields.title,
                administration: currentStatement.fields.administration,
                organisation: currentStatement.fields.organisation_name,
                assigned_slot: getAssignedSlot(slot.columnId),
                status: currentStatement.post_status,
                link: currentStatement.permalink,
                type:
                  currentStatement.categories[0]?.name.length > 0
                    ? currentStatement.categories[0]?.name
                    : 'Policy statement',
                tag: currentStatement.tags[0]?.name,
                thumbnail: currentStatement.thumbnail,
              };
              // If list type is full list, add additional columns
              if (get().gridOptions.listType === 'fulllist') {
                newRow = {
                  ...newRow,
                  ...{
                    submission_date: dayjs(currentStatement.post_date).format(
                      'DD/MM/YYYY'
                    ),
                    preferred_date: dayjs(
                      currentStatement.fields.preferred_slot,
                      'DD-MM-YYYY A'
                    ).format('DD/MM/YYYY A'),
                    second_choice: dayjs(
                      currentStatement.fields.second_choice,
                      'DD-MM-YYYY A'
                    ).format('DD/MM/YYYY A'),
                    third_choice: dayjs(
                      currentStatement.fields.third_choice,
                      'DD-MM-YYYY A'
                    ).format('DD/MM/YYYY A'),
                    contact: `${currentStatement.fields.contact_prefix} ${currentStatement.fields.contact_first_name} ${currentStatement.fields.contact_last_name}`,
                    contact_title: currentStatement.fields.contact_title,
                    contact_email: currentStatement.fields.contact_email,
                    contact_phone: currentStatement.fields.contact_phone,
                  },
                };
              }

              // If list type is piclist, add additional columns
              if (get().gridOptions.listType === 'piclist') {
                newRow = {
                  ...newRow,
                  ...{
                    picture: currentStatement.fields.small_photo?.sizes?.medium,
                  },
                };
              }

              // If list type is cardlist, add additional columns
              if (
                get().gridOptions.listType === 'card' &&
                currentStatement.post_status === 'publish'
              ) {
                newRow = {
                  ...newRow,
                  ...{
                    picture: currentStatement.fields.small_photo?.sizes?.large,
                    url: currentStatement.permalink,
                  },
                };
              }
              // Push new row to datagrid rows array
              dataGrid.rows.push(newRow);
            });

          // APPLY FILTERS
          // If date filter
          if (get().filters.date.length > 0) {
            dataGrid.rows = dataGrid.rows.filter(
              (row) => row.assigned_slot === get().filters.date
            );
          }
          // If country filter
          if (get().filters.country.length > 0) {
            dataGrid.rows = dataGrid.rows.filter(
              (row) => row.country === get().filters.country
            );
          }
          // If type filter
          if (get().filters.type.length > 0) {
            dataGrid.rows = dataGrid.rows.filter(
              (row) => row.type === get().filters.type
            );
          }
          // If search filter
          if (get().filters.search.length > 0) {
            dataGrid.rows = dataGrid.rows.filter(
              (row) =>
                row.speaker
                  .toLowerCase()
                  .includes(get().filters.search.toLowerCase()) ||
                row.speaker_title
                  .toLowerCase()
                  .includes(get().filters.search.toLowerCase()) ||
                row.administration
                  ?.toLowerCase()
                  .includes(get().filters.search.toLowerCase()) ||
                row.organisation
                  ?.toLowerCase()
                  .includes(get().filters.search.toLowerCase()) ||
                row.contact
                  ?.toLowerCase()
                  .includes(get().filters.search.toLowerCase())
            );
          }
          // Filter publish only
          if (get().settings.params.displaypublishedonly) {
            dataGrid.rows = dataGrid.rows.filter(
              (row) => row.status === 'publish'
            );
          }
          // Save the datagrid to the store
          set((state) => ({ ...state, MUIdata: dataGrid }));
          // Set loading to false
          get().toggleLoading();
        }
      });
  },
}));

// If dev env add devtools
mountStoreDevtool('Zustand store', useStore);
if (process.env.NODE_ENV === 'development') {
}
export default useStore;
